import React from 'react';
import clsx from 'clsx';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { dataNotAvailable } from '../../../../assets/hostedassets';
import styles from './GstOverview.module.scss';

export interface GstSubTabEntry {
    id: string;
    label: string;
    disabled?: boolean;
    tooltip?: string;
}

export type GstSubTabNoDataMap = Record<string, { tooltip?: string }>;

interface GstSubTabBarProps {
    subTabs: GstSubTabEntry[];
    activePill: string;
    onSelect: (pillId: string) => void;
    noDataPills?: GstSubTabNoDataMap;
    // Arbitrary content rendered at the right-hand end of the bar, after the pills.
    // Generic slot — this component stays agnostic of what callers put here.
    trailing?: React.ReactNode;
}

const DEFAULT_NO_DATA_TOOLTIP = 'Data not available, please connect or upload data to access this module';

function GstSubTabBar({ subTabs, activePill, onSelect, noDataPills, trailing }: GstSubTabBarProps) {
    // The bar still renders with no pills when it has trailing content: subTabs come from the
    // Polus dashboard config, which arrives async and is empty on first paint, so returning
    // null here would make anything in the trailing slot flicker out — or stay missing for a
    // PNO whose GST tab has no sub-tabs configured.
    if (!subTabs?.length && !trailing) return null;
    return (
        <div className={styles.SubTabBar}>
            <div className={styles.PillBar} role="tablist">
                {subTabs.map((pill) => {
                    const noDataEntry = noDataPills?.[pill.id];
                    const isNoData = !!noDataEntry;
                    const isDisabled = !!pill.disabled || isNoData;
                    const isActive = activePill === pill.id;
                    const pillNode = (
                        <button
                            key={pill.id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            aria-disabled={isDisabled}
                            className={clsx(styles.Pill, {
                                [styles.PillActive]: isActive,
                                [styles.PillDisabled]: isDisabled,
                            })}
                            onClick={isDisabled ? undefined : () => onSelect(pill.id)}
                        >
                            <span>{pill.label}</span>
                            {isNoData && <img src={dataNotAvailable} alt="" className={styles.PillNoDataIcon} aria-hidden="true" />}
                        </button>
                    );
                    if (!isDisabled) return pillNode;
                    const tooltipText = isNoData ? noDataEntry?.tooltip || DEFAULT_NO_DATA_TOOLTIP : pill.tooltip || 'Coming soon';
                    return (
                        <Tooltip key={pill.id} content={tooltipText} direction="bottom" fitContent usePortal>
                            {pillNode}
                        </Tooltip>
                    );
                })}
            </div>
            {trailing && <div className={styles.SubTabBarTrailing}>{trailing}</div>}
        </div>
    );
}

export default GstSubTabBar;
